import { useState } from 'react';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';
import { type IssuedValueMap } from '../../hooks/useIssuedData';
import { type ClientInfo } from '../../types';
import { useTranslation } from '../../i18n/i18n';
import EmbeddedLayout from '../../layouts/EmbeddedLayout';
import FlowFooter from '../../components/FlowFooter';
import LoadingState from '../../components/LoadingState';
import DataTableLayout from '../../components/DataTableLayout';
import DataTable from '../../components/DataTable';

type Props = {
  client?: ClientInfo;
  requestedDataGroups?: RequestedDataGroup[];
  requestedDataLoading?: boolean;
  issuedValueMap: IssuedValueMap;
  onAccept: () => void;
  onCancel: () => void;
  onBack?: () => void;
};

export default function EmbeddedIssuanceReviewPage({
  client,
  requestedDataGroups,
  requestedDataLoading,
  issuedValueMap,
  onAccept,
  onCancel,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const clientName = client?.name || t('dataReview.unknownClient');

  if (
    requestedDataLoading ||
    !requestedDataGroups ||
    requestedDataGroups.length === 0
  ) {
    return (
      <EmbeddedLayout>
        <LoadingState title={t('dataReview.loadingNames')} />
      </EmbeddedLayout>
    );
  }

  return (
    <EmbeddedLayout
      footer={
        <FlowFooter
          primaryLabel={t('dataReview.acceptReceive')}
          onPrimary={onAccept}
          secondaryLabel={onBack ? t('abort.goBack') : t('agentStart.cancel')}
          onSecondary={onBack ?? onCancel}
        />
      }
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-8 w-full">
        <div className="flex flex-col items-center gap-3 text-center max-w-lg">
          <h2 className="text-2xl font-semibold text-foreground">
            {t('dataReview.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('dataReview.subtitleIssuance', { clientName })}
          </p>
        </div>

        <div className="w-full max-w-2xl">
          <DataTableLayout
            groups={requestedDataGroups}
            selectedIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
            issuedByKey="dataReview.issuedBy"
            variant="desktop"
          >
            {(activeGroup) => {
              const credentialValues = issuedValueMap.get(
                activeGroup.credentialUuid,
              );
              return (
                <DataTable
                  group={activeGroup}
                  columns={[
                    {
                      header: t('dataReview.attribute'),
                      render: (f) => f.name,
                    },
                    {
                      header: t('dataReview.value'),
                      render: (f) =>
                        credentialValues?.get(f.attributeUuid) ?? '-',
                    },
                  ]}
                />
              );
            }}
          </DataTableLayout>
        </div>
      </div>
    </EmbeddedLayout>
  );
}
